import { AdjustmentType } from '@/lib/constants';

type SqlExecutor = (sql: string, params?: unknown[]) => Promise<unknown>;

/**
 * Cura retroativa da fatura zumbi (ADR 0068) — roda no boot, via backfill com DONE_KEY.
 *
 * Soft-deleta artefatos de fatura PENDENTES (despesa prevista "Fatura X" e pagamento não
 * confirmado) que ficaram sem dono e por isso congelaram: cartão excluído/inexistente ou
 * fatura inexistente (referência pendurada de restore/purge, que escrevem com FK OFF).
 * Também remove as duplicatas históricas de INVOICE_EXPENSE da mesma fatura (corrida
 * anterior à fila do `invoiceExpenseQueue`): o heal só atualiza a mais antiga, então é
 * ela que fica.
 *
 * Nada aqui toca linha confirmada — pagamento que debitou conta é histórico e permanece.
 *
 * O executor vem por parâmetro: o chamador real é o `initDatabase` (db/client), e
 * importar o client daqui criaria ciclo no boot; no teste entra o SQLite real do
 * dbHarness.
 */
export async function cleanupZombieInvoiceArtifacts(execute: SqlExecutor): Promise<void> {
    const now = new Date().toISOString();

    // Artefato pendente sem dono: fatura inexistente, ou fatura de cartão morto/inexistente.
    await execute(
        `UPDATE transactions
            SET deleted_at = ?, updated_at = ?
          WHERE deleted_at IS NULL
            AND is_confirmed = 0
            AND adjustment_type IN (?, ?)
            AND (
                invoice_id IS NULL
                OR invoice_id NOT IN (SELECT id FROM invoices)
                OR invoice_id IN (
                    SELECT i.id
                      FROM invoices i
                      LEFT JOIN credit_cards c ON c.id = i.credit_card_id
                     WHERE c.id IS NULL OR c.deleted_at IS NOT NULL
                )
            )`,
        [now, now, AdjustmentType.INVOICE_EXPENSE, AdjustmentType.INVOICE_PAYMENT],
    );

    // Duplicata de despesa prevista da mesma fatura: sobrevive só a mais antiga (menor
    // rowid — é a que `ensureInvoiceExpenseTransaction` encontra e atualiza).
    await execute(
        `UPDATE transactions
            SET deleted_at = ?, updated_at = ?
          WHERE deleted_at IS NULL
            AND is_confirmed = 0
            AND adjustment_type = ?
            AND EXISTS (
                SELECT 1 FROM transactions older
                 WHERE older.invoice_id = transactions.invoice_id
                   AND older.adjustment_type = transactions.adjustment_type
                   AND older.deleted_at IS NULL
                   AND older.is_confirmed = 0
                   AND older.rowid < transactions.rowid
            )`,
        [now, now, AdjustmentType.INVOICE_EXPENSE],
    );
}
